<template>
  <div class="review-questions-panel">
    <div class="summary-wrap">
      <p class="caption">
        <template v-if="exam.completed">
          Review your responses and explore the explanations for the correct answers.
        </template>
        <template v-else>
          Take a final look and revisit your answers if necessary before submitting your test.
        </template>
      </p>

      <h6>Summary:</h6>
      <div class="summary-info">
        <template v-if="exam.completed">
          <div class="question-type">
            <div class="question-box correct">
              {{ $store.getters['exams/correct'] }}
            </div>
            <span>Correct</span>
          </div>
          <div class="question-type">
            <div class="question-box incorrect">
              {{ $store.getters['exams/incorrect'] }}
            </div>
            <span>Incorrect</span>
          </div>
          <div class="question-type">
            <div class="question-box flagged">
              {{ $store.getters['exams/flagged'] }}
            </div>
            <span>Flagged <span class="flagged">(correct/incorrect)</span></span>
          </div>
        </template>
        <template v-else>
          <div class="question-type">
            <div class="question-box answered">
              {{ $store.getters['exams/answered'] }}
            </div>
            <span>Answered</span>
          </div>
          <div class="question-type">
            <div class="question-box not-answered">
              {{ $store.getters['exams/notAnswered'] }}
            </div>
            <span>Not Answered</span>
          </div>
          <div class="question-type">
            <div class="question-box flagged">
              {{ $store.getters['exams/flagged'] }}
            </div>
            <span>Flagged (answered/not answered)</span>
          </div>
        </template>
      </div>

      <ul v-if="answers">
        <li
          v-for="(question, idx) in exam.questions"
          :key="idx"
          class="question-box"
          :class="{
            'not-answered': !answers[question.id].answer,
            answered: answers[question.id].answer,
            flagged: answers[question.id].flag,
            correct: answers[question.id].correct && exam.completed,
            incorrect: !answers[question.id].correct && exam.completed,
            current: currentQuestion.id === question.id
          }"
          @click="$store.commit('exams/setCurrentQuestionIdx', idx)"
        >
          {{ idx + 1 }}
        </li>
      </ul>

      <nuxt-link
        v-if="exam.completed"
        :to="resultsLink"
        class="btn-primary-transparent done-reviewing"
      >
        Done reviewing
      </nuxt-link>
      <template v-else>
        <nuxt-link
          :to="$config.project === 'lms' ? `/exams/${$route.params.id}/finalize/` : `/resources-center/exams/${$route.params.id}/finalize/`"
          class="btn-primary finalize"
        >
          Finalize
        </nuxt-link>
        <p class="caption">
          Submit exam on the next page
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  computed: {
    ...mapState({
      exam: (state) => state.exams.exam,
      answers: (state) => (Object.keys(state.exams.answers).length === 0 ? null : state.exams.answers),
    }),
    ...mapGetters('exams', ['currentQuestion']),
    resultsLink() {
      if (this.exam.type === 'practice') {
        return `/exams/practice/${this.exam.id}/results/`;
      }

      if (this.exam.type === 'course') {
        return `/exams/course/${this.exam.courseId}/results/`;
      }

      return `/exams/track/${this.exam.trackId}/results/`;
    },
  },
};
</script>

<style lang="scss" scoped>
.review-questions-panel {
  width: 412px;
  right: -412px;
  transition: all 0.3s linear;
  position: fixed;
  bottom: 0;
  top: 0;
  box-sizing: border-box;
  background: var(--main-dark-light-2);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
  color: var(--main-light);
  z-index: 1;
  padding-top: 70px;

  h5 {
    cursor: pointer;
    font-weight: 500;
    display: flex;
    align-items: center;
    color: var(--accent);
    height: 64px;
    margin: 0 var(--spacing-28);

    svg {
      width: 12px;
      margin-right: var(--spacing-24);

      path {
        fill: var(--accent);
      }
    }
  }

  .summary-wrap {
    padding: 6px var(--spacing-lg) var(--spacing-24);
    overflow: auto;
    height: 100%;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--main-light-shade-3);
      border-radius: 10px;
    }

    p.caption {
      color: var(--main-light-shade-3);
      margin: 0;
      line-height: 23px;
      font-weight: 500;
    }

    h6 {
      color: var(--main-light);
      opacity: 0.8;
      margin-top: var(--spacing-28);
      margin-bottom: var(--spacing-8);
    }

    .question-box {
      width: 40px;
      height: 40px;
      text-align: center;
      display: inline-block;
      margin: 0 var(--spacing-4) var(--spacing-8);
      border-radius: 6px;
      border: 1px solid var(--accent-shade-3);
      color: var(--accent);
      font-weight: 500;
      cursor: pointer;
      font-size: 18px;
      line-height: 40px;
      background: var(--main-dark-shade-2);
      position: relative;
      overflow: hidden;

      &.answered {
        color: var(--accent);
        border-color: var(--accent-shade-2);

        &:not(div):not(.current):hover {
          background-color: rgba(140, 218, 227, 0.5);
        }
      }

      &.not-answered {
        border-color: var(--main-light-shade-3);
        color: var(--main-light);

        &:not(div):not(.current):hover {
          background-color: rgba(249, 250, 251, 0.5);
        }
      }

      &.correct {
        color: var(--main-green-light-3);
        border-color: var(--main-green-shade-1);

        &:not(div):not(.current):hover {
          background-color: rgba(105, 238, 134, 0.5);
        }
      }

      &.incorrect {
        color: var(--main-red-light-3);
        border-color: var(--main-red-light-3);

        &:not(div):not(.current):hover {
          background-color: rgba(255, 80, 80, 0.4);
          border-color: var(--main-red);
          color: var(--main-red);
        }
      }

      &.flagged {
        &:before {
          content: '';
          position: absolute;
          right: -15px;
          top: -15px;
          width: 24px;
          height: 24px;
          background: var(--main-yellow);
          transform: rotate(45deg);
        }
      }

      &.current.answered {
        background: var(--accent-shade-1);
        border: 1px solid var(--accent-shade-1);
        color: var(--main-light);
      }

      &.current.not-answered {
        background: var(--main-light-shade-2);
        border: 1px solid var(--main-light-shade-2);
        color: var(--darker-gray);
      }

      &.current.incorrect {
        color: var(--main-light);
        background-color: var(--main-red);
        border-color: var(--main-red);
      }

      &.current.correct {
        color: var(--main-light);
        background-color: var(--main-green);
        border-color: var(--main-green);
      }

      &:nth-child(6n) {
        margin-right: 0;
      }
    }

    .summary-info {
      font-size: 14px;
      margin-bottom: var(--spacing-28);

      .question-type {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-8);

        .question-box {
          width: 24px;
          height: 24px;
          font-size: 10px;
          border-radius: 4px;
          line-height: 22px;
          margin: 0;
          margin-right: var(--spacing-8);
          cursor: unset;

          &.flagged {
            color: var(--main-yellow);
            border-color: var(--main-yellow);
          }
        }
      }
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      margin-left: -4px;
      margin-right: -4px;
      font-size: 0;
    }

    .finalize,
    .done-reviewing {
      width: 100%;
      margin-top: var(--spacing-28);
      margin-bottom: var(--spacing-12);
      line-height: 23px;
    }

    .finalize + p {
      text-align: center;
      color: var(--main-light-shade-4);
      opacity: 0.8;
    }
  }

  &.expand {
    right: 0;
  }
}

.fa {
  .review-questions-panel {
    background: var(--main-dark-light-1);

    .summary-wrap {
      .question-box {
        background: var(--main-dark-light-1);
      }

      .summary-info {
        .question-type {
          .flagged {
            opacity: 0.8;
          }
        }
      }

      .question-box:not(.incorrect):not(.correct) {
        &.answered {
          color: var(--accent-light-3);
          border-color: var(--accent-shade-1);

          &:not(div):not(.current):hover {
            background: var(--accent-light-3);
            border-color: var(--accent-light-3);
            color: var(--main-light);
          }
        }

        &.current.answered {
          background: var(--accent);
          border-color: var(--accent);
          color: var(--main-light);
        }
      }

      .done-reviewing {
        margin-top: var(--spacing-20);
      }
    }
  }
}

@include bp($bp-laptop) {
  .review-questions-panel {
    width: 370px;

    .summary-wrap {
      padding: var(--spacing-24) var(--spacing-50) 0 var(--spacing-40);

      .finalize {
        padding-top: var(--spacing-8);
        padding-bottom: var(--spacing-8);
      }
    }
  }
}
</style>
